import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Search,
  Filter,
  RefreshCw,
  Clock,
  User,
  Building2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  Paperclip,
  MoreVertical,
  ChevronLeft,
  Tag,
  UserCheck,
  MessageCircle,
  Inbox,
  Archive,
  Star,
  Flag,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  getAdminSupportTickets,
  getAdminSupportStats,
  updateAdminSupportTicket,
  getSupportTicketDetail,
  addTicketMessage,
  getSupportAgents,
} from '../services/api';
import { toast } from 'sonner';

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20', icon: Inbox },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: Clock },
  waiting_on_customer: { label: 'Waiting', color: 'bg-purple-500/10 text-purple-500 border-purple-500/20', icon: User },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Archive },
};

const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  medium: { label: 'Medium', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  high: { label: 'High', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
  urgent: { label: 'Urgent', color: 'bg-red-500/10 text-red-500 border-red-500/20' },
};

const categoryConfig = {
  general: { label: 'General', color: 'bg-slate-500/10 text-slate-400' },
  billing: { label: 'Billing', color: 'bg-emerald-500/10 text-emerald-400' },
  technical: { label: 'Technical', color: 'bg-blue-500/10 text-blue-400' },
  security: { label: 'Security', color: 'bg-red-500/10 text-red-400' },
  feature_request: { label: 'Feature Request', color: 'bg-purple-500/10 text-purple-400' },
};

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Message input
  const [newMessage, setNewMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter, priorityFilter, categoryFilter]);

  useEffect(() => {
    if (ticketDetail) {
      scrollToBottom();
    }
  }, [ticketDetail?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;
      
      const [ticketsRes, statsRes, agentsRes] = await Promise.all([
        getAdminSupportTickets(params),
        getAdminSupportStats(),
        getSupportAgents(),
      ]);
      
      setTickets(ticketsRes.data.tickets);
      setStats(ticketsRes.data.stats);
      setAgents(agentsRes.data.agents);
    } catch (error) {
      console.error('Failed to fetch support data:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingDetail(true);
    try {
      const response = await getSupportTicketDetail(ticket.id);
      setTicketDetail(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateTicket = async (field, value) => {
    if (!selectedTicket) return;
    
    try {
      await updateAdminSupportTicket(selectedTicket.id, { [field]: value });
      toast.success(`Ticket ${field} updated`);
      
      // Refresh ticket detail and list
      const response = await getSupportTicketDetail(selectedTicket.id);
      setTicketDetail(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    
    setSendingMessage(true);
    try {
      await addTicketMessage(selectedTicket.id, {
        message: newMessage,
        is_internal: isInternalNote,
      });
      
      setNewMessage('');
      setIsInternalNote(false);
      
      // Refresh ticket detail
      const response = await getSupportTicketDetail(selectedTicket.id);
      setTicketDetail(response.data);
      
      toast.success(isInternalNote ? 'Internal note added' : 'Reply sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const filteredTickets = tickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.created_by_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading && !tickets.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="admin-support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-purple-500" />
            </div>
            Support Center
          </h1>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Inbox className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.open || 0}</p>
              <p className="text-xs text-slate-500">Open</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-4 h-4 text-amber-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.in_progress || 0}</p>
              <p className="text-xs text-slate-500">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <User className="w-4 h-4 text-purple-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.waiting_on_customer || 0}</p>
              <p className="text-xs text-slate-500">Waiting</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-red-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.urgent || 0}</p>
              <p className="text-xs text-slate-500">Urgent</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{stats?.resolved || 0}</p>
              <p className="text-xs text-slate-500">Resolved</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Ticket List */}
        <div className={`${selectedTicket ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-96 min-h-0`}>
          {/* Search & Filters */}
          <div className="space-y-3 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-800 text-white"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-white flex-1">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priorityFilter || 'all'} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-white flex-1">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all">All Priority</SelectItem>
                  {Object.entries(priorityConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Ticket List */}
          <Card className="flex-1 bg-slate-900 border-slate-800 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="divide-y divide-slate-800">
                {filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    className={`p-4 cursor-pointer transition-colors hover:bg-slate-800/50 ${
                      selectedTicket?.id === ticket.id ? 'bg-slate-800/50 border-l-2 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-slate-500 font-mono">{ticket.ticket_number}</span>
                          <Badge className={priorityConfig[ticket.priority]?.color || ''} variant="outline">
                            {ticket.priority}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-white truncate">{ticket.subject}</h3>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 text-slate-500">
                        <User className="w-3 h-3" />
                        <span className="truncate max-w-[120px]">{ticket.created_by_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={statusConfig[ticket.status]?.color || ''} variant="outline">
                          {statusConfig[ticket.status]?.label || ticket.status}
                        </Badge>
                        <span className="text-slate-500">{formatDate(ticket.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredTickets.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No tickets found</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>

        {/* Ticket Detail */}
        {selectedTicket && (
          <Card className="flex-1 bg-slate-900 border-slate-800 flex flex-col min-h-0">
            {/* Ticket Header */}
            <div className="p-4 border-b border-slate-800">
              <div className="flex items-center gap-3 mb-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedTicket(null)}
                  className="lg:hidden"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-slate-500 font-mono">{ticketDetail?.ticket_number}</span>
                    <Badge className={categoryConfig[ticketDetail?.category]?.color || ''}>
                      {categoryConfig[ticketDetail?.category]?.label || ticketDetail?.category}
                    </Badge>
                  </div>
                  <h2 className="font-heading font-semibold text-lg text-white">{ticketDetail?.subject}</h2>
                </div>
              </div>
              
              {/* Ticket Actions */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Select 
                    value={ticketDetail?.status} 
                    onValueChange={(v) => handleUpdateTicket('status', v)}
                  >
                    <SelectTrigger className="w-36 h-8 bg-slate-800 border-slate-700 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(statusConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Priority</Label>
                  <Select 
                    value={ticketDetail?.priority} 
                    onValueChange={(v) => handleUpdateTicket('priority', v)}
                  >
                    <SelectTrigger className="w-28 h-8 bg-slate-800 border-slate-700 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(priorityConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Assignee</Label>
                  <Select 
                    value={ticketDetail?.assigned_to || 'unassigned'} 
                    onValueChange={(v) => handleUpdateTicket('assigned_to', v === 'unassigned' ? '' : v)}
                  >
                    <SelectTrigger className="w-36 h-8 bg-slate-800 border-slate-700 text-sm">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-800 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-white">{ticketDetail?.created_by_name}</span>
                <span className="text-slate-500">({ticketDetail?.created_by_email})</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-slate-400">Tenant: {ticketDetail?.tenant_id?.slice(0, 8)}...</span>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loadingDetail ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : (
                <div className="space-y-4">
                  {ticketDetail?.messages?.map((msg, index) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.is_internal
                            ? 'bg-amber-500/10 border border-amber-500/20'
                            : msg.sender_type === 'admin'
                            ? 'bg-blue-500/10 border border-blue-500/20'
                            : 'bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium ${
                            msg.sender_type === 'admin' ? 'text-blue-400' : 'text-slate-300'
                          }`}>
                            {msg.sender_name}
                          </span>
                          {msg.is_internal && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs">Internal Note</Badge>
                          )}
                          <span className="text-xs text-slate-500">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-slate-300 text-sm whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Reply Box */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox
                  id="internal"
                  checked={isInternalNote}
                  onCheckedChange={setIsInternalNote}
                />
                <Label htmlFor="internal" className="text-sm text-slate-400 flex items-center gap-1">
                  {isInternalNote ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  Internal note (not visible to customer)
                </Label>
              </div>
              <div className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={isInternalNote ? "Add internal note..." : "Type your reply..."}
                  className="bg-slate-800 border-slate-700 text-white resize-none"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className={isInternalNote ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}
                >
                  {sendingMessage ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Press Cmd/Ctrl + Enter to send</p>
            </div>
          </Card>
        )}

        {/* Empty State */}
        {!selectedTicket && (
          <div className="hidden lg:flex flex-1 items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-400 mb-2">Select a ticket</h3>
              <p className="text-sm text-slate-500">Choose a ticket from the list to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
